import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, startGoogleLogin } from "@/lib/auth";
import { useLocale } from "@/lib/locale";
import { LOCALE_NAMES, SUPPORTED_LOCALES } from "@/lib/i18n";
import { Bell, LogOut, Film, User, Mail, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth() || {};
  const { locale, setLocale, t } = useLocale();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 glass-strong border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
          <div className="relative h-9 w-9 rounded-md bg-amber/90 flex items-center justify-center shadow-[0_0_24px_-6px_rgba(245,166,35,0.7)]">
            <Film className="h-5 w-5 text-obsidian" strokeWidth={2} />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-2xl tracking-tight text-white">MovioHub</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-amber/80">beta</span>
          </div>
        </Link>

        <nav className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-zinc-300 hover:text-amber hover:bg-white/5 text-sm font-heading"
                data-testid="locale-picker"
              >
                <Globe className="h-4 w-4" /> {LOCALE_NAMES[locale]}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-obsidian-surface border-white/10 text-zinc-200 min-w-[150px]">
              {SUPPORTED_LOCALES.map((loc) => (
                <DropdownMenuItem
                  key={loc}
                  onClick={() => setLocale(loc)}
                  className={loc === locale ? "text-amber" : ""}
                  data-testid={`locale-${loc}`}
                >
                  {LOCALE_NAMES[loc]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <>
              <Button
                variant="ghost"
                className="text-zinc-300 hover:text-amber hover:bg-white/5 font-heading"
                onClick={() => navigate("/dashboard")}
                data-testid="header-notifications-btn"
              >
                <Bell className="h-4 w-4 mr-2" /> {t("my_alerts")}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="h-9 w-9 rounded-full overflow-hidden border border-white/10 hover:border-amber transition"
                    data-testid="user-menu-trigger"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full bg-amber/20 text-amber flex items-center justify-center font-heading">
                        {user.name?.[0] || user.email?.[0]}
                      </div>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-obsidian-surface border-white/10 text-zinc-200 min-w-[200px]">
                  <DropdownMenuLabel className="font-heading">
                    <div className="text-sm">{user.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/5" />
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                    <User className="h-4 w-4 mr-2" /> {t("profile")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/friends")} data-testid="menu-friends">
                    <User className="h-4 w-4 mr-2" /> Friends
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="menu-messages">
                    <Mail className="h-4 w-4 mr-2" /> {t("inbox")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/critics")} data-testid="menu-critics">
                    <User className="h-4 w-4 mr-2" /> Critics
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                    <Bell className="h-4 w-4 mr-2" /> {t("dashboard")}
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin" className="text-amber">
                      <User className="h-4 w-4 mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/5" />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" /> {t("sign_out")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button
              onClick={startGoogleLogin}
              className="bg-amber hover:bg-amber-hover text-obsidian font-heading font-medium rounded-md px-4"
              data-testid="header-login-btn"
            >
              {t("sign_in")}
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
}
